// services/database.js

import { getCurrentUser } from 'aws-amplify/auth';
import proxyapi from './ProxyApi';


/**
 * @deprecated Use confirmAndCreateUser in auth service instead
 * This function is maintained for backward compatibility
 */
export const saveUserEmail = async (email, userId) => {
  try {
    const response = await proxyapi.post('/api/v1/users', {
      email: email,
      userId: userId   // pass the AWS user id from signup
    });
    return response.data;
  } catch (error) {
    console.error('Error saving user email:', error);
    throw error;
  }
};

// Function to get current user ID
export const getCurrentUserId = async () => {
  try {
    const user = await getCurrentUser();
    return user.userId;
  } catch (error) {
    console.error('Error getting user ID:', error);
    throw error;
  }
};

// Function to create new project
export const createProject = async (projectData) => {
  try {
    const userId = await getCurrentUserId();
    const response = await proxyapi.post('/api/v1/projects', {
      userId,
      ...projectData
    });
    return response.data;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

export const updateProjectStyle = async (projectId, styleChoices) => {
  try {
    if (!projectId) {
      throw new Error('Project ID is required');
    }
    
    console.log('Updating style for project:', projectId);
    
    const response = await proxyapi.put(`/api/v1/projects/${projectId}/style`, {
      styleChoices
    });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error updating project style choices:', error);
    throw new Error(error.response?.data?.message || error.message);
  }
};

export const getProjectDetails = async (projectId) => {
  try {
    const response = await proxyapi.get(`/api/v1/projects/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project details:', error);
    throw error;
  }
};

export const retrieveViews = async (projectId) => {
  try {
    const response = await proxyapi.get(`/api/v1/projects/${projectId}/views`);
    return response.data.views;
  } catch (error) {
    console.error('Error retrieving views:', error);
    throw error;
  }
};

export const deleteView = async (viewId) => {
  try {
    await proxyapi.delete(`/api/v1/views/${viewId}`);
  } catch (error) {
    console.error('Error deleting view:', error);
    throw error;
  }
};

export const retrieveFlows = async (projectId) => {
  try {
    const response = await proxyapi.get(`/api/v1/projects/${projectId}/flows`);
    return response.data.flows;
  } catch (error) {
    console.error('Error retrieving flows:', error);
    throw error;
  }
};

export const deleteFlow = async (flowId) => {
  try {
    await proxyapi.delete(`/api/v1/flows/${flowId}`);
  } catch (error) {
    console.error('Error deleting flow:', error);
    throw error;
  }
};

export const updateProject = async (projectId, projectData) => {
  try {
    const response = await proxyapi.put(`/api/v1/projects/${projectId}`, projectData);
    return response.data;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

export const getProjectsByUserId = async () => {
  try {
    const userId = await getCurrentUserId();
    const response = await proxyapi.get(`/api/v1/users/${userId}/projects`);
    return response.data.projects;
  } catch (error) {
    console.error('Error fetching user projects:', error);
    throw error;
  }
};

// Retrieve views for a specific project
export const retrieveViews2= async (projectId) => {
  try {
    // Example GET request if your backend has routes like /api/v1/views?projectId=...
    const response = await proxyapi.get(`/api/v1/views?projectId=${projectId}`);
    return response.data.views || [];
  } catch (error) {
    console.error("Error retrieving views:", error);
    return [];
  }
};

// Function to get a single view's details
export const getView = async (viewId) => {
  try {
    const response = await proxyapi.get(`/api/v1/views/${viewId}`);
    return response.data; // Returns the full view object
  } catch (error) {
    console.error('Error fetching view details:', error);
    throw error;
  }
};

export const updateViewTitle = async (viewId, title) => {
  try {
    const response = await proxyapi.put(`/api/v1/views/${viewId}/title`, { title });
    return response.data;
  } catch (error) {
    console.error("Error updating view title:", error);
    throw error;
  }
};

// Create a new flow
export const createFlow = async (projectId, flowData) => {
  try {
    const response = await proxyapi.post("/api/v1/flows", {
      projectId: flowData.projectId,
      title: flowData.flowTitle,
      time_created: flowData.timeCreated,
      number_of_views: flowData.numberOfViews,
      order: flowData.order
    });
    return response.data; // e.g. { status: 'success', flowId: '...' }
  } catch (error) {
    console.error("Error creating flow:", error);
    throw error;
  }
};

// Retrieve flow details (including embedded HTML for each view version)
export const getFlow = async (flowId) => {
  try {
    const response = await proxyapi.get(`/api/v1/flows/${flowId}`);
    return response.data; // e.g. { status: 'success', flow: { ... } }
  } catch (error) {
    console.error("Error fetching flow:", error);
    return null;
  }
};

export const updateFlow = async (flowId, flowData) => {
  try {
    const response = await proxyapi.put(`/api/v1/flows/${flowId}`, {
      ...flowData,
      projectId: flowData.projectId
    });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error updating flow:', error);
    throw error;
  }
};

export const deleteVersion = async (viewId, versionId) => {
  try {
    const response = await proxyapi.delete(`/api/v1/views/${viewId}/versions/${versionId}`);
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    return true;
  } catch (error) {
    console.error('Error deleting version:', error);
    throw error;
  }
};

export const deleteProject = async (projectId) => {
  try {
    const userId = await getCurrentUserId();

    const response = await proxyapi.delete(`/api/v1/projects/${projectId}`, {
      headers: {
        'Content-Type': 'application/json'
      },
      data: {
        userId
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

// services/database.js

export const searchProjects = async (searchTerm = '', sortOption = 'dateDesc') => {
  try {
    const userId = await getCurrentUserId();
    
    // Map frontend sort options to backend parameters
    let sortBy, sortOrder;
    
    switch (sortOption) {
      case 'dateAsc':
        sortBy = 'createdAt';
        sortOrder = 'asc';
        break;
      case 'dateDesc':
        sortBy = 'createdAt';
        sortOrder = 'desc';
        break;
      case 'titleAsc':
        sortBy = 'title';
        sortOrder = 'asc';
        break;
      case 'titleDesc':
        sortBy = 'title';
        sortOrder = 'desc';
        break;
      case 'relevance':  // NEW: Atlas Search relevance sorting
        sortBy = 'relevance';
        sortOrder = 'desc';
        break;
      default:
        // If there's a search term, default to relevance; otherwise date
        if (searchTerm && searchTerm.trim()) {
          sortBy = 'relevance';
          sortOrder = 'desc';
        } else {
          sortBy = 'createdAt';
          sortOrder = 'desc';
        }
    }
    
    // Build query parameters
    const params = new URLSearchParams();
    if (searchTerm) params.append('q', searchTerm);
    params.append('sort_by', sortBy);
    params.append('sort_order', sortOrder);
    
    const response = await proxyapi.get(
      `/api/v1/users/${userId}/projects/search?${params.toString()}`
    );
    
    return response.data.projects;
  } catch (error) {
    console.error('Error searching projects:', error);
    throw error;
  }
};